/*
 * Valenor — Reviews carousel.
 * - Clones the card list × 3 so both directions are always scrollable from
 *   mount ("truly infinite" on both sides). On scroll we silently jump back
 *   to the middle section when the user drifts into the outer clones.
 * - Autoplays 7 s to the right, pauses on hover / touch / focus / hidden tab.
 * - Scrolling vertically never moves the strip: we only hijack horizontal
 *   pans and never autoplay while the user is actively touching it.
 */
(() => {
  if (typeof window === 'undefined' || window.__vlnReviewsInit) return;
  window.__vlnReviewsInit = true;

  const INTERVAL_MS = 7000;
  const reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const init = () => {
    const grid = document.querySelector('.vln-reviews-showcase__grid');
    if (!grid) return;
    const cards = Array.from(grid.children);
    if (cards.length <= 1) return;

    // --- Clone cards to enable bidirectional infinite scroll ---
    // Prepend a copy before, append a copy after.
    const prepend = cards.map((c) => c.cloneNode(true));
    const append = cards.map((c) => c.cloneNode(true));
    prepend.reverse().forEach((c) => grid.insertBefore(c, grid.firstChild));
    append.forEach((c) => grid.appendChild(c));

    // All children (3 copies). Tag origin copies for faster jump math.
    const total = grid.children.length;
    const copyLen = cards.length;

    const firstMiddle = () => grid.children[copyLen];
    const sectionWidth = () => {
      const first = grid.children[0];
      const nextStart = grid.children[copyLen];
      return nextStart.offsetLeft - first.offsetLeft;
    };

    // Position scroll at the start of the middle section.
    const center = () => {
      const s = firstMiddle();
      if (s) grid.scrollLeft = s.offsetLeft;
    };

    // After layout settles (images decode, fonts load), centre.
    requestAnimationFrame(() => requestAnimationFrame(center));
    window.addEventListener('load', center, { once: true });

    // --- Wrap when user drifts into outer copies ---
    let suppressWrap = false;
    grid.addEventListener('scroll', () => {
      if (suppressWrap) return;
      const left = grid.scrollLeft;
      const width = sectionWidth();
      const middleStart = width;
      const middleEnd = width * 2;
      if (left < middleStart - width * 0.25) {
        suppressWrap = true;
        grid.scrollLeft = left + width;
        requestAnimationFrame(() => (suppressWrap = false));
      } else if (left > middleEnd + width * 0.25) {
        suppressWrap = true;
        grid.scrollLeft = left - width;
        requestAnimationFrame(() => (suppressWrap = false));
      }
    });

    // --- Wheel passthrough (always attached, even with reduced motion) ---
    // When the user wheels over the carousel, forward any vertical-dominant
    // delta to the page scroll. Horizontal-dominant delta (shift+wheel,
    // trackpad horizontal) is allowed to scroll the carousel natively.
    grid.addEventListener(
      'wheel',
      (e) => {
        if (Math.abs(e.deltaY) >= Math.abs(e.deltaX)) {
          e.preventDefault();
          e.stopPropagation();
          window.scrollBy({ top: e.deltaY, left: 0, behavior: 'auto' });
        }
      },
      { passive: false, capture: true }
    );

    if (reduceMotion) return;

    // --- Autoplay that stops once the user takes control ---
    // Any pointerdown / touchstart on the grid = user is interacting.
    // Any scroll whose origin is NOT our own scrollBy = user is driving.
    // After either happens, autoplay stays OFF until the next page load.
    let stopped = false;
    let programmaticUntil = 0;

    const stop = () => {
      if (!stopped) {
        stopped = true;
      }
    };

    grid.addEventListener('pointerdown', stop);
    grid.addEventListener('touchstart', stop, { passive: true });
    grid.addEventListener('keydown', stop);

    grid.addEventListener('scroll', () => {
      // `programmaticUntil` is a grace window set before each autoplay
      // scrollBy; any scroll event during that window is from us, not the
      // user.
      if (performance.now() < programmaticUntil) return;
      stop();
    });

    // Only autoplay when the section is in the viewport.
    let inView = true;
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) inView = entry.isIntersecting;
        },
        { threshold: 0.15 }
      );
      io.observe(grid);
    }

    setInterval(() => {
      if (stopped || !inView) return;
      const first = grid.children[0];
      if (!first) return;
      const step = first.getBoundingClientRect().width + 20; /* gap */
      programmaticUntil = performance.now() + 1200; /* cover smooth-scroll */
      grid.scrollBy({ left: step, behavior: 'smooth' });
    }, INTERVAL_MS);

    // Desktop prev / next arrows — these count as "our" scrolls so autoplay
    // keeps running after a click.
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-vln-reviews-arrow]');
      if (!btn) return;
      e.preventDefault();
      const first = grid.children[0];
      if (!first) return;
      const step = first.getBoundingClientRect().width + 20;
      const delta = btn.dataset.vlnReviewsArrow === 'next' ? step : -step;
      programmaticUntil = performance.now() + 1200;
      grid.scrollBy({ left: delta, behavior: 'smooth' });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
